import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { productService } from "../../services/product.service";
import { ProductTable, type Product } from "../../components/ProductTable/ProductTable";
import PageHeader from "../../components/common/PageHeader";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { formatNumber } from "@/utils/formatters";

export function ProductsList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);

  const fetchProducts = async (cursor?: string) => {
    try {
      const response = await productService.findAll(cursor);
      setProducts((prevProducts) => {
        const existingIds = new Set(prevProducts.map((p) => p.id));
        const newProducts = response.products.filter(
          (p: Product) => !existingIds.has(p.id)
        );
        return [...prevProducts, ...newProducts];
      });
      setEndCursor(response.pagination.endCursor);
      setHasNextPage(response.pagination.hasNextPage);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleLoadMore = () => {
    if (!hasNextPage || isLoadingMore) return;
    setIsLoadingMore(true);
    fetchProducts(endCursor || undefined);
  };

  const handleProductClick = (productId: string) => {
    // Extract the product ID from the Shopify GID format
    const id = productId.replace("gid://shopify/Product/", "");
    navigate(`/brand/products/insights/${id}`);
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Products" />

      <DashboardCard title="All products" bodyClassName="px-3 pb-3">
        <ProductTable
          products={products}
          onProductClick={handleProductClick}
          isLoading={isLoading}
        />
        {!isLoading && products.length > 0 && (
          <div className="mt-3 flex items-center justify-between border-t border-border px-2 pt-3 text-sm text-muted-foreground">
            <span>
              {hasNextPage
                ? `${formatNumber(products.length)} products loaded`
                : `${formatNumber(products.length)} products`}
            </span>
            {hasNextPage && (
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                {isLoadingMore ? "Loading" : "Load more"}
              </button>
            )}
          </div>
        )}
      </DashboardCard>
    </div>
  );
}
